import { useEffect, useRef, useCallback, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useI18n } from '@/context/I18nContext';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Content rendered between header and scrollable body (e.g. tab bars) */
  stickyContent?: ReactNode;
  footer?: ReactNode;
  /** Prevent closing (e.g. during import) */
  preventClose?: boolean;
}

export function Dialog({ open, onClose, title, subtitle, children, footer, stickyContent, preventClose }: Props) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);

  const handleClose = useCallback(() => {
    if (preventClose) return;
    onClose();
  }, [onClose, preventClose]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      document.body.classList.add('modal-open');
    } else if (!open && el.open) {
      el.close();
      document.body.classList.remove('modal-open');
    }
  }, [open]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        handleClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) handleClose();
      }}
      className="
        w-full max-w-[720px] max-h-[85vh]
        bg-bg-raised border border-border-subtle rounded-xl
        text-text-primary
        p-0 m-auto overflow-hidden
        max-sm:max-w-full max-sm:max-h-full max-sm:h-full max-sm:rounded-none
      "
    >
      <div className="flex flex-col h-full max-h-[85vh] max-sm:max-h-full">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-border-subtle shrink-0">
          <div>
            <h3 className="text-base font-semibold tracking-tight">{title}</h3>
            {subtitle && (
              <p className="text-xs text-text-muted mt-1">{subtitle}</p>
            )}
          </div>
          {!preventClose && (
            <button
              onClick={handleClose}
              aria-label={t('common.close')}
              className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer shrink-0"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Sticky content (between header and scrollable body) */}
        {stickyContent && (
          <div className="px-6 border-b border-border-subtle shrink-0">
            {stickyContent}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex gap-2 px-6 py-4 border-t border-border-subtle shrink-0">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}
